import React, { useState } from "react";
import { Link, Navigate, useParams } from "react-router-dom";
import Navbar from "../components/Navbar";
import CallbackForm from "../components/CallbackForm";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { findCondition, CONDITIONS, ICON_MAP } from "../lib/conditions";
import { ArrowRight, AlertTriangle, Stethoscope, CheckCircle2, ChevronDown, Phone } from "lucide-react";

export default function ConditionDetail() {
  const { slug } = useParams();
  const c = findCondition(slug);
  if (!c) return <Navigate to="/conditions" replace />;

  const Icon = ICON_MAP[c.icon] || ICON_MAP.Brain;
  const related = CONDITIONS.filter((x) => x.slug !== c.slug).slice(0, 4);
  const waText = encodeURIComponent(`Hi, I need a second opinion for ${c.title}. Can you guide me?`);
  const waHref = `https://wa.me/918317674160?text=${waText}`;

  return (
    <div className="min-h-screen bg-slate-50">
      <Navbar />

      {/* Hero */}
      <section className={`bg-gradient-to-br ${c.color} text-white`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14">
          <Link to="/conditions" className="inline-flex items-center gap-1.5 text-sm text-white/80 hover:text-white font-semibold" data-testid="condition-back">
            ← Health Library
          </Link>
          <div className="mt-5 flex items-start gap-4">
            <div className="w-14 h-14 rounded-2xl bg-white/15 backdrop-blur grid place-items-center shrink-0">
              <Icon className="w-7 h-7"/>
            </div>
            <div className="flex-1">
              <h1 className="font-heading text-4xl sm:text-5xl font-bold leading-tight">{c.title}</h1>
              <p className="mt-2 text-white/90 max-w-2xl">{c.blurb}</p>
            </div>
          </div>
          {c.hero_stat && (
            <div className="mt-6 inline-flex items-center gap-3 bg-white/10 backdrop-blur border border-white/20 rounded-xl px-5 py-3">
              <div className="font-heading text-3xl font-bold">{c.hero_stat}</div>
              <div className="text-sm text-white/90 leading-tight max-w-[280px]">{c.hero_stat_label}</div>
            </div>
          )}
        </div>
      </section>

      <article className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 grid grid-cols-1 lg:grid-cols-12 gap-8">
        <div className="lg:col-span-8 space-y-8">
          {/* Overview */}
          <section>
            <h2 className="font-heading text-2xl font-semibold text-slate-900">What is {c.title}?</h2>
            <p className="mt-3 text-slate-700 leading-relaxed">{c.overview}</p>
          </section>

          {/* Second-opinion criteria */}
          <Card className="p-6 border-teal-200 bg-gradient-to-br from-white to-teal-50/40" data-testid="second-opinion-criteria">
            <div className="flex items-center gap-2 text-teal-800">
              <Stethoscope className="w-5 h-5"/>
              <h2 className="font-heading text-xl font-semibold">When a second opinion really helps</h2>
            </div>
            <ul className="mt-4 space-y-3">
              {c.second_opinion_reasons.map((r, i) => (
                <li key={i} className="flex items-start gap-2.5">
                  <CheckCircle2 className="w-5 h-5 text-teal-600 mt-0.5 shrink-0"/>
                  <span className="text-sm text-slate-700 leading-relaxed">{r}</span>
                </li>
              ))}
            </ul>
            <div className="mt-5 flex flex-wrap gap-3">
              <Link to={`/login?as=patient&tag=${encodeURIComponent(c.tag)}`}>
                <Button className="h-11 px-5 bg-teal-700 hover:bg-teal-800 text-white font-semibold" data-testid="condition-get-opinion-cta">
                  Get my second opinion <ArrowRight className="w-4 h-4 ml-1.5"/>
                </Button>
              </Link>
              <a href={waHref} target="_blank" rel="noreferrer" data-testid="condition-whatsapp-cta">
                <Button variant="outline" className="h-11 px-5">WhatsApp us</Button>
              </a>
            </div>
          </Card>

          {/* Red flags */}
          {c.red_flags?.length > 0 && (
            <Card className="p-6 border-rose-200 bg-rose-50/40" data-testid="red-flags-card">
              <div className="flex items-center gap-2 text-rose-700">
                <AlertTriangle className="w-5 h-5"/>
                <h2 className="font-heading text-xl font-semibold">Red flags — call 108 / 102 immediately</h2>
              </div>
              <ul className="mt-4 space-y-2.5">
                {c.red_flags.map((r, i) => (
                  <li key={i} className="flex items-start gap-2.5 text-sm text-rose-900 leading-relaxed">
                    <span className="mt-1 w-1.5 h-1.5 rounded-full bg-rose-500 shrink-0"></span>
                    {r}
                  </li>
                ))}
              </ul>
              <div className="mt-4 flex gap-3">
                <a href="tel:108" className="inline-flex items-center gap-1.5 text-sm font-semibold text-rose-700 hover:text-rose-800 border border-rose-300 rounded-md px-3 py-1.5">
                  <Phone className="w-3.5 h-3.5"/> Ambulance: 108
                </a>
                <a href="tel:102" className="inline-flex items-center gap-1.5 text-sm font-semibold text-rose-700 hover:text-rose-800 border border-rose-300 rounded-md px-3 py-1.5">
                  <Phone className="w-3.5 h-3.5"/> 102
                </a>
              </div>
            </Card>
          )}

          {/* FAQs */}
          {c.faqs?.length > 0 && (
            <section>
              <h2 className="font-heading text-2xl font-semibold text-slate-900">Frequently asked questions</h2>
              <div className="mt-4 space-y-2" data-testid="condition-faqs">
                {c.faqs.map((f, i) => (
                  <Faq key={i} f={f} i={i}/>
                ))}
              </div>
            </section>
          )}

          {/* Related conditions */}
          <section>
            <h2 className="font-heading text-2xl font-semibold text-slate-900">Other conditions we help with</h2>
            <div className="mt-4 grid grid-cols-2 sm:grid-cols-4 gap-3">
              {related.map((r) => {
                const RIcon = ICON_MAP[r.icon] || ICON_MAP.Brain;
                return (
                  <Link key={r.slug} to={`/conditions/${r.slug}`} className="group" data-testid={`related-condition-${r.slug}`}>
                    <Card className="p-4 border-slate-200 lift h-full text-center">
                      <div className={`w-9 h-9 mx-auto rounded-lg bg-gradient-to-br ${r.color} text-white grid place-items-center mb-2`}>
                        <RIcon className="w-4.5 h-4.5"/>
                      </div>
                      <div className="text-sm font-semibold text-slate-900 group-hover:text-teal-700 transition leading-tight">{r.title}</div>
                    </Card>
                  </Link>
                );
              })}
            </div>
          </section>
        </div>

        {/* Sticky sidebar */}
        <aside className="lg:col-span-4">
          <div className="lg:sticky lg:top-28 space-y-4">
            <CallbackForm source={`condition:${c.slug}`}/>
          </div>
        </aside>
      </article>

      {/* Floating WhatsApp CTA — visible on mobile + desktop. Pre-fills the
          patient's condition so the WhatsApp ops team has instant context. */}
      <a
        href={waHref}
        target="_blank"
        rel="noreferrer"
        className="fixed bottom-5 right-5 z-50 inline-flex items-center gap-2 bg-green-600 hover:bg-green-700 text-white font-semibold rounded-full pl-4 pr-5 py-3 shadow-lg shadow-green-600/30 transition-transform hover:scale-105"
        data-testid="condition-whatsapp-floating"
        aria-label={`Talk to NeuroOpinion on WhatsApp about ${c.title}`}
      >
        <svg viewBox="0 0 32 32" className="w-5 h-5 fill-current" aria-hidden="true">
          <path d="M19.11 17.205c-.372 0-1.088 1.39-1.518 1.39a.63.63 0 0 1-.315-.1c-.802-.402-1.504-.817-2.163-1.447-.545-.516-1.146-1.29-1.46-1.963a.426.426 0 0 1-.073-.215c0-.33.99-.945.99-1.49 0-.143-.73-2.09-.832-2.335-.143-.372-.214-.487-.6-.487-.187 0-.36-.043-.53-.043-.302 0-.53.115-.746.315-.688.645-1.03 1.318-1.044 2.264v.114c-.015.99.472 1.977 1.103 2.738 1.4 1.69 3.265 2.652 5.215 3.214.42.143.83.27 1.262.27.978 0 2.034-.5 2.434-1.32.158-.317.255-.668.255-1.04v-.115c0-.343-.387-.487-.515-.515-.143-.043-.617-.286-.86-.286-.143 0-.286.143-.302.486zM16 0c8.836 0 16 7.164 16 16 0 2.864-.78 5.55-2.115 7.9l2.07 7.45-7.62-2.06A15.95 15.95 0 0 1 16 32C7.165 32 0 24.835 0 16 0 7.165 7.165 0 16 0z"/>
        </svg>
        Talk on WhatsApp
      </a>
    </div>
  );
}

function Faq({ f, i }) {
  const [open, setOpen] = useState(i === 0);
  return (
    <div className="border border-slate-200 rounded-lg bg-white overflow-hidden">
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className="w-full text-left px-4 py-3 flex items-center justify-between gap-3 hover:bg-slate-50"
        data-testid={`faq-toggle-${i}`}
      >
        <span className="font-semibold text-slate-800 text-sm">{f.q}</span>
        <ChevronDown className={`w-4 h-4 text-slate-500 shrink-0 transition-transform ${open ? "rotate-180" : ""}`}/>
      </button>
      {open && (
        <div className="px-4 pb-4 text-sm text-slate-600 leading-relaxed">
          {f.a}
        </div>
      )}
    </div>
  );
}
